import type { InfiniteData, Query, QueryClient } from "@tanstack/react-query";
import type { InferRouterOutputs } from "@orpc/server";

import { orpc } from "@/client";
import type { API } from "@/server";
import type { ConversationCursor } from "@/api/router/messages/schemas";

type ConversationPage = InferRouterOutputs<API>["messages"]["conversation"];
type History = InfiniteData<ConversationPage, ConversationCursor>;
type Delta = InferRouterOutputs<API>["messages"]["delta"];
interface ConversationMeta {
	personId: number;
}

const caches = new WeakMap<QueryClient, MessageCache>();

export class MessageCache {
	private readonly active = new Map<string, Promise<void>>();
	private readonly resetNeeded = new Set<string>();
	private readonly requested = new Map<string, boolean>();

	constructor(private client: QueryClient) {}

	static for(client: QueryClient) {
		let cache = caches.get(client);

		if (!cache) {
			cache = new MessageCache(client);
			caches.set(client, cache);
		}

		return cache;
	}

	async person(personId: number, reset = false) {
		const queries = this.client.getQueryCache().findAll({
			queryKey: orpc.messages.conversation.key({ input: { person_id: personId } }),
			type: "active",
		});
		await Promise.all(queries.map((query) => this.sync(query, reset)));
	}

	async all(reset = false) {
		const queries = this.client
			.getQueryCache()
			.findAll({ queryKey: orpc.messages.conversation.key() });

		if (reset) {
			for (const query of queries) {
				this.resetNeeded.add(query.queryHash);
			}
		}
		await Promise.all(
			queries.filter((query) => query.isActive()).map((query) => this.sync(query, reset)),
		);
	}

	private sync(query: Query, reset: boolean) {
		this.requested.set(
			query.queryHash,
			reset ||
				this.resetNeeded.has(query.queryHash) ||
				this.requested.get(query.queryHash) === true,
		);
		const active = this.active.get(query.queryHash);

		if (active) {
			return active;
		}

		const work = this.drain(query).finally(() => this.active.delete(query.queryHash));
		this.active.set(query.queryHash, work);

		return work;
	}

	private async drain(query: Query) {
		while (this.requested.has(query.queryHash)) {
			const reset = this.requested.get(query.queryHash) === true;
			this.requested.delete(query.queryHash);
			const history = query.state.data as History | undefined;
			const meta = query.meta?.conversation as ConversationMeta | undefined;

			if (!history || !meta) {
				return;
			}

			const oldest = history.pages.toReversed().find((page) => page.messages.length > 0)
				?.messages[0];
			let delta = await orpc.messages.delta.call({
				person_id: meta.personId,
				since: history.pages[0].revision,
				oldest: oldest ? { id: oldest.id, sent_at: oldest.sent_at } : null,
				reset,
			});

			const replace = reset || delta.reset;

			if (delta.reset) {
				delta = await orpc.messages.delta.call({
					person_id: meta.personId,
					since: 0,
					oldest: oldest ? { id: oldest.id, sent_at: oldest.sent_at } : null,
					reset: true,
				});
			}

			if (this.client.getQueryCache().get(query.queryHash) !== query) {
				return;
			}

			this.client.setQueryData<History>(query.queryKey, (current) => {
				if (!current) {
					return current;
				}

				if (current !== history) {
					this.requested.set(
						query.queryHash,
						replace || this.requested.get(query.queryHash) === true,
					);
					return current;
				}

				this.resetNeeded.delete(query.queryHash);
				return this.apply(current, delta, replace);
			});
		}
	}

	private apply(history: History, delta: Delta, reset: boolean): History {
		const changed = new Map(delta.messages.map((message) => [message.id, message]));
		const removed = new Set(delta.removed);
		const known = new Set(
			history.pages.flatMap((page) => page.messages.map((message) => message.id)),
		);
		const pages = history.pages.map((page) => ({
			...page,
			state: delta.state,
			revision: delta.revision,
			messages: page.messages.flatMap((message) => {
				if (removed.has(message.id) || (reset && !changed.has(message.id))) {
					return [];
				}

				return [changed.get(message.id) || message];
			}),
		}));
		const additions = delta.messages.filter((message) => !known.has(message.id));

		for (const message of additions) {
			const page =
				pages.find((candidate) => {
					const first = candidate.messages.at(0);
					return (
						first &&
						(message.sent_at > first.sent_at ||
							(message.sent_at.getTime() === first.sent_at.getTime() && message.id >= first.id))
					);
				}) || pages[0];
			page.messages.push(message);
		}

		for (const page of pages) {
			page.messages.sort(
				(a, b) => a.sent_at.getTime() - b.sent_at.getTime() || a.id.localeCompare(b.id),
			);
		}

		return { ...history, pages };
	}
}
